import { useState } from 'react'
import { useOrganization } from '@/lib/hooks/useOrganization'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Building, Plus, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

export default function OrganizationSelector() {
  const { 
    organizations, 
    currentOrganization, 
    loading, 
    switchOrganization,
    createOrganization 
  } = useOrganization()
  
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [creating, setCreating] = useState(false)
  const [organizationName, setOrganizationName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const handleOrganizationChange = (orgId: string) => {
    if (orgId === 'create-new') {
      setShowCreateDialog(true)
    } else {
      switchOrganization(orgId)
    }
  }

  const handleCreateOrganization = async () => {
    if (!organizationName.trim()) {
      setError('Organization name is required')
      return
    }

    try {
      setCreating(true)
      setError(null)
      
      await createOrganization(organizationName.trim())
      
      // Reset form and close dialog
      setOrganizationName('')
      setShowCreateDialog(false)
      
      toast.success('Organization created successfully')
    } catch (err: any) {
      setError(err.message || 'Failed to create organization')
    } finally {
      setCreating(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center space-x-2 text-sm text-gray-600">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading organizations...</span>
      </div>
    )
  }

  // If user has no organizations, show a create button instead
  if (organizations.length === 0) {
    return (
      <>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setShowCreateDialog(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Organization
        </Button>

        {/* Create Organization Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Your First Organization</DialogTitle>
              <DialogDescription>
                Organizations help you manage multiple companies or projects separately.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="org-name">Organization Name *</Label>
                <Input
                  id="org-name"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  placeholder="e.g., My Company"
                  disabled={creating}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreateOrganization} 
                disabled={creating || !organizationName.trim()}
              >
                {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Organization
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    )
  }

  return (
    <>
      <div className="flex items-center space-x-2">
        <Building className="h-5 w-5 text-gray-600" />
        <Select
          value={currentOrganization?.id || ''}
          onValueChange={handleOrganizationChange}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select organization" />
          </SelectTrigger>
          <SelectContent>
            {organizations.map((org) => (
              <SelectItem key={org.id} value={org.id}>
                <div className="flex items-center justify-between w-full">
                  <span>{org.name}</span>
                  {org.role === 'owner' && (
                    <span className="text-xs text-gray-500 ml-2">Owner</span>
                  )}
                </div>
              </SelectItem>
            ))}
            <SelectItem value="create-new">
              <div className="flex items-center">
                <Plus className="h-4 w-4 mr-2" />
                Create New Organization
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Create Organization Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Organization</DialogTitle>
            <DialogDescription>
              Create a new organization to manage a separate company or team.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="org-name">Organization Name *</Label>
              <Input
                id="org-name"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="e.g., My Company"
                disabled={creating}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateOrganization} 
              disabled={creating || !organizationName.trim()}
            >
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Organization
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}